import React, { useMemo } from 'react';
import { CalendarDaysIcon, ClockIcon, UserIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { WeeklyAvailability, MemberAvailabilityBlock } from '../../types/availability';

interface AvailabilityStatsProps {
  availability?: WeeklyAvailability | null;
  monthlyBlocks?: MemberAvailabilityBlock[] | null;
  viewMode?: 'week' | 'month' | 'day';
  currentDate?: Date;  // M6 fix: needed to filter blocks to current month only
}

// Helper to calculate hours from availability blocks
const calculateHoursFromBlocks = (blocks: MemberAvailabilityBlock[]) => {
  let totalHours = 0;
  let bookedHours = 0;

  blocks.forEach(block => {
    // Breaks are non-bookable — exclude them from capacity/utilization stats
    // (they remain in the blocks list so the calendar can still render them).
    if (block.block_type === 'break') return;
    const [startH, startM] = block.start_time.split(':').map(Number);
    const [endH, endM] = block.end_time.split(':').map(Number);
    const blockHours = (endH - startH) + (endM - startM) / 60;
    totalHours += blockHours;
    if (block.is_booked) {
      bookedHours += blockHours;
    }
  });

  const utilization = totalHours > 0 ? Math.round((bookedHours / totalHours) * 100) : 0;

  return {
    total_available_hours: Math.round(totalHours * 100) / 100,
    total_booked_hours: Math.round(bookedHours * 100) / 100,
    utilization_percentage: utilization,
  };
};

const AvailabilityStats: React.FC<AvailabilityStatsProps> = ({
  availability,
  monthlyBlocks,
  viewMode = 'week',
  currentDate,
}) => {
  // Calculate stats from blocks for accurate hour calculation
  const calculatedStats = useMemo(() => {
    const zeroStats = {
      total_available_hours: 0,
      total_booked_hours: 0,
      utilization_percentage: 0,
    };

    // M6 fix: Month view should ONLY use monthly data, not fall back to weekly
    if (viewMode === 'month') {
      if (!monthlyBlocks || monthlyBlocks.length === 0) {
        return zeroStats;
      }
      // Filter to only include blocks within the current month
      // The calendar grid includes greyed-out days from adjacent months,
      // but stats should only reflect the current month
      let filteredBlocks = monthlyBlocks;
      if (currentDate) {
        const monthStartStr = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        const monthEndStr = format(endOfMonth(currentDate), 'yyyy-MM-dd');
        filteredBlocks = monthlyBlocks.filter(block =>
          block.date >= monthStartStr && block.date <= monthEndStr
        );
      }
      return calculateHoursFromBlocks(filteredBlocks);
    }

    // For week view, calculate from weekly availability blocks (not pre-calculated values)
    if (availability && availability.availability_blocks && availability.availability_blocks.length > 0) {
      return calculateHoursFromBlocks(availability.availability_blocks);
    }

    // Fallback to zeros
    return zeroStats;
  }, [viewMode, monthlyBlocks, availability, currentDate]);

  const availableHours = Math.round((calculatedStats.total_available_hours - calculatedStats.total_booked_hours) * 100) / 100;

  const stats = [
    {
      name: viewMode === 'month' ? 'Monthly Hours' : 'Weekly Hours',
      value: calculatedStats.total_available_hours,
      icon: CalendarDaysIcon,
      color: 'text-clinic-primary',
      bgColor: 'bg-clinic-primary/10',
    },
    {
      name: 'Available',
      value: `${availableHours} hrs`,
      icon: ClockIcon,
      color: 'text-clinic-primary-dark',
      bgColor: 'bg-clinic-cream',
    },
    {
      name: 'Booked',
      value: `${calculatedStats.total_booked_hours} hrs`,
      icon: UserIcon,
      color: 'text-clinic-gold',
      bgColor: 'bg-clinic-gold/10',
    },
    {
      name: 'Utilization',
      value: `${calculatedStats.utilization_percentage}%`,
      icon: ChartBarIcon,
      color: 'text-clinic-accent',
      bgColor: 'bg-clinic-accent/10',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <div key={stat.name} className="bg-white p-6 rounded-card shadow-sm border border-clinic-primary-lighter">
          <div className="flex items-center">
            <div className={`p-3 rounded-full ${stat.bgColor}`}>
              <stat.icon className={`h-6 w-6 ${stat.color}`} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">{stat.name}</p>
              <p className="text-2xl font-semibold text-clinic-dark">{stat.value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default React.memo(AvailabilityStats);